import {SDKRequest, type ShareActivityLinkType, DiscordCommand} from '../../../../../../shared/DiscordActivityTypes.ts'
import {type DiscordContentFrameCommunicationHandler} from '../DiscordContentFrameCommunicationHandler.ts'
import {type ColonistMembershipType, type ColonistOneTimePurchaseType} from '@colonist/types'
import {SDKRequestValidateAction} from './SDKRequestValidateAction.ts'
import {ColonistCoinProduct, type ColonistCoinType} from '../../../../../../shared/store/ColonistCoinProduct.ts'
import {ColonistMembershipProduct} from '../../../../../../shared/store/ColonistMembershipProduct.ts'
import {ColonistOneTimePurchaseProduct} from '../../../../../../shared/store/ColonistOneTimePurchaseProduct.ts'
import {AnalyticEventCategory, AnalyticEventType, type ShareActivityLinkAnalytics} from '../../../../../../shared/entity_datas/EntityDatas.ts'
import {type AjaxDataTypes} from '../../../../../../shared/AjaxDataTypes.ts'
import {CA} from '../../../../../shared/scripts/analytics/Analytics.ts'

export class SDKRequestHandler {
    private readonly contentFrameCommunicationHandler: DiscordContentFrameCommunicationHandler

    constructor(contentFrameCommunicationHandler: DiscordContentFrameCommunicationHandler) {
        this.contentFrameCommunicationHandler = contentFrameCommunicationHandler
    }

    processSDKRequest(sdkRequest: SDKRequest, data: any) {
        const validator = new SDKRequestValidateAction(sdkRequest, data)
        if(!validator.isValid) return

        this.handleSDKRequest(validator)
    }

    handleSDKRequest(sdkRequest: SDKRequestValidateAction) {
        switch(sdkRequest.type) {
            case SDKRequest.PurchaseMembership: this.purchaseMembership(sdkRequest.data as ColonistMembershipType); break
            case SDKRequest.PurchaseGift: this.purchaseGift(sdkRequest.data as ColonistOneTimePurchaseType); break
            case SDKRequest.PurchaseCoins: this.purchaseCoins(sdkRequest.data as ColonistCoinType); break
            case SDKRequest.InviteFriends: this.inviteFriends(); break
            case SDKRequest.ShareMoment: this.shareMoment(sdkRequest.data); break
            case SDKRequest.OpenExternalLink: this.openExternalLink(sdkRequest.data); break
            case SDKRequest.ShareActivityLink: void this.shareActivityLink(sdkRequest.data); break
            default: throw new Error('Invalid SDK Request')
        }
    }

    private purchaseMembership(membershipType: ColonistMembershipType) {
        const membershipProduct = ColonistMembershipProduct.getMembershipProduct(membershipType)
        void this.contentFrameCommunicationHandler.sendMessageToMount({
            command: DiscordCommand.Purchase,
            data: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                sku_id: membershipProduct.discordSKUId,
            },
        })
    }

    private purchaseGift(oneTimePurchaseType: ColonistOneTimePurchaseType) {
        const oneTimePurchaseProduct = ColonistOneTimePurchaseProduct.getOneTimePurchaseProduct(oneTimePurchaseType)
        void this.contentFrameCommunicationHandler.sendMessageToMount({
            command: DiscordCommand.Purchase,
            data: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                sku_id: oneTimePurchaseProduct.discordSKUId,
            },
        })
    }

    private purchaseCoins(coinType: ColonistCoinType) {
        const coinProduct = ColonistCoinProduct.getPurchaseProduct(coinType)
        void this.contentFrameCommunicationHandler.sendMessageToMount({
            command: DiscordCommand.Purchase,
            data: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                sku_id: coinProduct.discordSKUId,
            },
        })
    }

    private inviteFriends() {
        void this.contentFrameCommunicationHandler.sendMessageToMount({
            command: DiscordCommand.InviteFriends,
        })
    }

    private shareMoment(file: File) {
        void this.contentFrameCommunicationHandler.sendMessageToMount({
            command: DiscordCommand.ShareMoment,
            data: {
                imageFile: file,
            },
        })
    }

    private openExternalLink(url: string) {
        void this.contentFrameCommunicationHandler.sendMessageToMount({
            command: DiscordCommand.OpenExternalLink,
            data: {
                url: url,
            },
        })
    }

    private async shareActivityLink(data: ShareActivityLinkType) {
        SDKRequestHandler.sendShareActivityLinkAnalytic(data.analyticsPopupTriggered)

        const result = await this.contentFrameCommunicationHandler.sendMessageToMount({
            command: DiscordCommand.ShareActivityLink,
            data: {
                message: data.message,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                referrer_id: data.referrerId,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                custom_id: data.referralTypeId,
            },
        })

        if(result?.success != true) return

        SDKRequestHandler.sendShareActivityLinkAnalytic(data.analyticsPopupSuccess)
    }

    static sendShareActivityLinkAnalytic(analyticType: ShareActivityLinkAnalytics) {
        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ReferralAnalytics,
            category: AnalyticEventCategory.ShareActivityLink,
            floatValue: analyticType,
        }
        void CA.sendAnalyticEvent(eventData)
    }
}
